import { Directive, ElementRef, HostListener, inject } from '@angular/core';

/**
 * A date input chosen from the calendar only. Typing, pasting and dropping
 * are blocked, so a value like "3424-10-11" can't be keyed in, and a click
 * (or Enter / Space) opens the browser's date picker.
 *
 * Tab and Shift+Tab still move focus, and Escape still closes the picker.
 * Pair it with [min] / [max]: the picker honours them, typing would not.
 */
@Directive({
  selector: 'input[type="date"][appPickerOnlyDate]',
  standalone: true,
})
export class PickerOnlyDateDirective {
  private readonly el = inject<ElementRef<HTMLInputElement>>(ElementRef);

  private static readonly ALLOWED_KEYS = new Set(['Tab', 'Escape']);

  @HostListener('click')
  open(): void {
    this.showPicker();
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (PickerOnlyDateDirective.ALLOWED_KEYS.has(event.key)) {
      return;
    }
    event.preventDefault();
    if (event.key === 'Enter' || event.key === ' ') {
      this.showPicker();
    }
  }

  @HostListener('paste', ['$event'])
  @HostListener('drop', ['$event'])
  block(event: Event): void {
    event.preventDefault();
  }

  private showPicker(): void {
    const input = this.el.nativeElement;
    // showPicker() needs a user gesture and throws in a few older browsers;
    // there the native calendar button still works.
    try {
      input.showPicker?.();
    } catch {
      /* the field stays usable through its own calendar button */
    }
  }
}
